import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Form, Button, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

interface FieldConfig {
  id?: number;
  field_name: string;
  table_name: string;
  user_type: string;
  is_required: boolean;
  display_order: number;
  field_label: string;
  field_type: string;
  is_visible: boolean;
}

interface PossibleField {
  field_name: string;
  table_name: string;
  user_type: string;
  field_label: string;
  field_type: string;
}

type AllPossibleFields = {
  [userType: string]: PossibleField[];
};

type ConfigMap = {
  [userType: string]: {
    [fieldName: string]: FieldConfig;
  };
};

const RegistrationFieldEditor: React.FC = () => {
  const [allFields, setAllFields] = useState<AllPossibleFields>({});
  const [config, setConfig] = useState<ConfigMap>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<string>('customer');
  const { isAuthenticated } = useAuth();

  // Get token from localStorage
  const getAuthToken = () => localStorage.getItem('token');

  // Fetch all possible fields and config on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = getAuthToken();
        const [fieldsRes, configRes] = await Promise.all([
          axios.get('/ap/api/admin/registration-fields/available', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/ap/api/admin/registration-fields/config', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setAllFields(fieldsRes.data);
        // Build config map for quick lookup
        const configMap: ConfigMap = {};
        for (const c of configRes.data as FieldConfig[]) {
          if (!configMap[c.user_type]) configMap[c.user_type] = {};
          configMap[c.user_type][c.field_name] = c;
        }
        setConfig(configMap);
        setError('');
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load field configurations');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle checkbox toggle
  const handleToggle = async (userType: string, fieldName: string, key: 'is_visible' | 'is_required', value: boolean) => {
    const fieldConfig = config[userType]?.[fieldName];
    if (!fieldConfig) return;
    try {
      setLoading(true);
      const token = getAuthToken();
      await axios.patch('/ap/api/admin/registration-fields/config', [
        { id: fieldConfig.id, [key]: value }
      ], {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setConfig(prev => ({
        ...prev,
        [userType]: {
          ...prev[userType],
          [fieldName]: {
            ...fieldConfig,
            [key]: value
          }
        }
      }));
      setSuccess('Field configuration updated!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update field configuration');
    } finally {
      setLoading(false);
    }
  };

  // Render table for a user type
  const renderFieldTable = (userType: string) => {
    const possibleFields = allFields[userType] || [];
    if (loading && possibleFields.length === 0) {
      return <div className="text-center p-4"><Spinner animation="border" /></div>;
    }
    return (
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Field Name</th>
            <th>Label</th>
            <th>Type</th>
            <th>Visible</th>
            <th>Required</th>
          </tr>
        </thead>
        <tbody>
          {possibleFields.map(field => {
            const fieldConfig = config[userType]?.[field.field_name];
            return (
              <tr key={field.field_name}>
                <td>{field.field_name}</td>
                <td>{field.field_label}</td>
                <td>{field.field_type}</td>
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={!!fieldConfig?.is_visible}
                    onChange={e => handleToggle(userType, field.field_name, 'is_visible', e.target.checked)}
                    disabled={loading || !fieldConfig}
                  />
                </td>
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={!!fieldConfig?.is_required}
                    onChange={e => handleToggle(userType, field.field_name, 'is_required', e.target.checked)}
                    disabled={loading || !fieldConfig}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Registration Field Configuration</h1>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      <Card>
        <Card.Body>
          <p>
            Toggle which fields are visible and required for each account type. Changes are saved instantly.
          </p>
          <Tabs
            activeKey={currentTab}
            onSelect={tab => tab && setCurrentTab(tab)}
            id="registration-field-tabs"
            className="mb-4"
          >
            <Tab eventKey="customer" title="Customer">
              {renderFieldTable('customer')}
            </Tab>
            <Tab eventKey="business" title="Business">
              {renderFieldTable('business')}
            </Tab>
            <Tab eventKey="affiliate" title="Affiliate">
              {renderFieldTable('affiliate')}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RegistrationFieldEditor; 
